/* global BigInt */
/**
 * AES-256-GCM Encryption/Decryption with minute-based dynamic key derivation.
 *
 * KEY ROTATION: Every 5 minutes a new encryption key is derived.
 * PROTOCOL: The timestamp (timeslot) used for encryption is sent alongside
 * the encrypted payload as the X-Enc-Ts header. The other side uses this
 * timeslot to derive the same key for decryption.
 *
 * This logic MUST match the backend Java EncryptionUtil exactly.
 */

const ALGORITHM = "AES-GCM";
const IV_LENGTH = 12; // 96 bits
const KEY_LENGTH = 256; // bits
const PBKDF2_ITERATIONS = 65536;
const TIMESLOT_MINUTES = 5; // Key rotates every 5 minutes

// ─── Key Fragment Constants (scattered, not obvious) ────────────────────────
const K_FRAG_1 = "Mv";
const K_FRAG_2 = "Rk";
const K_FRAG_3 = "Pl";
const K_FRAG_4 = "At";

// Salt derivation seeds
const SALT_SEED_A = 0x4d56;
const SALT_SEED_B = 0x524b;
const PLATFORM_ID = "mvrk";

// Obfuscation constant for timeslot encoding (must match Java)
const TS_OBFUSCATE = 0x5a3c;

// Config secret
const CONFIG_SECRET =
  process.env.REACT_APP_ENCRYPTION_SECRET_KEY || "TRAVAIOTIVARAX256BitSecretKey!";

// ─── Timeslot Logic (must match Java exactly) ────────────────────────────────

/**
 * Get current timeslot: UTC epoch seconds / 60 / TIMESLOT_MINUTES.
 * Changes every 5 minutes.
 */
export function getCurrentTimeslot() {
  const epochSeconds = Math.floor(Date.now() / 1000);
  const totalMinutes = Math.floor(epochSeconds / 60);
  return Math.floor(totalMinutes / TIMESLOT_MINUTES);
}

/**
 * Encode timeslot to obfuscated string (for X-Enc-Ts header).
 * XOR with constants then Base64url encode the hex representation.
 * Must match Java encodeTimeslot().
 */
export function encodeTimeslot(timeslot) {
  const ts = BigInt(timeslot);
  const obfuscated = ts ^ (BigInt(TS_OBFUSCATE) << 16n) ^ BigInt(SALT_SEED_A);

  const hex = obfuscated >= 0n
    ? obfuscated.toString(16)
    : (obfuscated + (1n << 64n)).toString(16);

  const bytes = new TextEncoder().encode(hex);
  let base64 = btoa(String.fromCharCode(...bytes));
  base64 = base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return base64;
}

/**
 * Decode obfuscated timeslot string back to numeric timeslot.
 * Must match Java decodeTimeslot().
 */
export function decodeTimeslot(encoded) {
  let base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
  while (base64.length % 4 !== 0) base64 += "=";

  const binaryStr = atob(base64);
  const bytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  const hex = new TextDecoder().decode(bytes);

  const obfuscated = BigInt("0x" + hex);
  const timeslot = obfuscated ^ (BigInt(TS_OBFUSCATE) << 16n) ^ BigInt(SALT_SEED_A);
  return Number(timeslot);
}

// ─── Passphrase Builder (must match Java) ────────────────────────────────────

function buildPassphrase(configSecret) {
  let sb = "";
  sb += K_FRAG_3; // "Pl"
  sb += configSecret.substring(0, Math.min(8, configSecret.length));
  sb += K_FRAG_1; // "Mv"
  sb += PLATFORM_ID; // "mvrk"
  sb += K_FRAG_4; // "At"
  sb += configSecret.substring(Math.min(8, configSecret.length));
  sb += K_FRAG_2; // "Rk"
  sb += (SALT_SEED_A ^ SALT_SEED_B).toString(16);
  return sb;
}

// ─── Dynamic Salt (based on timeslot, must match Java computeSalt) ───────────

function computeSalt(timeslot) {
  const tsHigh = (timeslot >> 16) & 0xffff;
  const tsLow = timeslot & 0xffff;

  const mixed1 = (Math.imul(tsHigh, SALT_SEED_A) ^ Math.imul(tsLow, SALT_SEED_B) ^ Math.imul(timeslot, 0x1f)) | 0;
  const mixed2 = (Math.imul(tsLow, SALT_SEED_A) ^ Math.imul(tsHigh, 0x3d) ^ Math.imul(timeslot, SALT_SEED_B)) | 0;
  const mixed3 = (Math.imul(tsHigh + tsLow, SALT_SEED_A + SALT_SEED_B) ^ 0xdead) | 0;
  const mixed4 = (Math.imul(tsHigh ^ tsLow, SALT_SEED_A ^ SALT_SEED_B) + 0xbeef) | 0;

  const salt = new Uint8Array(16);
  salt[0] = (mixed1 >> 24) & 0xff;
  salt[1] = (mixed1 >> 16) & 0xff;
  salt[2] = (mixed1 >> 8) & 0xff;
  salt[3] = mixed1 & 0xff;
  salt[4] = (mixed2 >> 24) & 0xff;
  salt[5] = (mixed2 >> 16) & 0xff;
  salt[6] = (mixed2 >> 8) & 0xff;
  salt[7] = mixed2 & 0xff;
  salt[8] = (mixed3 >> 24) & 0xff;
  salt[9] = (mixed3 >> 16) & 0xff;
  salt[10] = (mixed3 >> 8) & 0xff;
  salt[11] = mixed3 & 0xff;
  salt[12] = (mixed4 >> 24) & 0xff;
  salt[13] = (mixed4 >> 16) & 0xff;
  salt[14] = (mixed4 >> 8) & 0xff;
  salt[15] = mixed4 & 0xff;

  const platformBytes = new TextEncoder().encode(PLATFORM_ID);
  for (let i = 0; i < salt.length; i++) {
    salt[i] ^= platformBytes[i % platformBytes.length];
  }

  return salt;
}

// ─── Key Derivation (PBKDF2-SHA256) ──────────────────────────────────────────

async function deriveKeyForTimeslot(timeslot) {
  const passphrase = buildPassphrase(CONFIG_SECRET);
  const salt = computeSalt(timeslot);

  if (!window.crypto || !window.crypto.subtle) {
    throw new Error("Web Crypto API not available. Cannot encrypt/decrypt.");
  }

  const keyMaterial = await window.crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(passphrase),
    "PBKDF2",
    false,
    ["deriveBits", "deriveKey"]
  );

  return window.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt.buffer,
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ["encrypt", "decrypt"]
  );
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Encrypt a plaintext string using the current timeslot's key.
 * Returns: { ciphertext: string (Base64), timeslot: string (obfuscated, for X-Enc-Ts header) }
 */
export async function encrypt(plaintext) {
  const ts = getCurrentTimeslot();
  const key = await deriveKeyForTimeslot(ts);

  const encoder = new TextEncoder();
  const data = encoder.encode(plaintext);

  const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  const ciphertextBuf = await window.crypto.subtle.encrypt(
    { name: ALGORITHM, iv },
    key,
    data
  );

  const combined = new Uint8Array(iv.length + ciphertextBuf.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertextBuf), iv.length);

  return {
    ciphertext: btoa(String.fromCharCode(...combined)),
    timeslot: encodeTimeslot(ts),
  };
}

/**
 * Decrypt a Base64-encoded AES-256-GCM payload using the provided timeslot.
 * The timeslot comes from the X-Enc-Ts response header.
 */
export async function decrypt(encryptedBase64, encodedTimeslot) {
  const ts = decodeTimeslot(encodedTimeslot);
  return decryptWithTimeslot(encryptedBase64, ts);
}

/**
 * Decrypt using a specific numeric timeslot.
 */
async function decryptWithTimeslot(encryptedBase64, timeslot) {
  const key = await deriveKeyForTimeslot(timeslot);

  const binaryStr = atob(encryptedBase64);
  const combined = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    combined[i] = binaryStr.charCodeAt(i);
  }

  const iv = combined.slice(0, IV_LENGTH);
  const ciphertext = combined.slice(IV_LENGTH);

  const decrypted = await window.crypto.subtle.decrypt(
    { name: ALGORITHM, iv },
    key,
    ciphertext
  );

  return new TextDecoder().decode(decrypted);
}
